/**
 * QuizRegenerateButton — small "Refresh with AI" button that lives on
 * every Quiz content section inside the LMS Lesson form.
 *
 * Clicking it opens a Popover with three inputs:
 *   - Topic  (defaults to the section title or lesson title)
 *   - Tier   (free/pro/business/enterprise)
 *   - Count  (1-10 questions)
 *
 * Then POSTs to `/api/admin/ai/regenerate-quiz` and hands the fresh
 * quiz items back to the parent via `onReplace(questions)`. The parent
 * (LessonForm) swaps them into the section's `quiz_data.questions`.
 *
 * Kept dumb-and-portable: no dependency on any parent hook, no direct
 * lessonForm access — the parent owns state.
 */
import React, { useContext, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../App';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '../../ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { RefreshCw, Sparkles } from 'lucide-react';

const TIER_LABEL = { free: 'Free', pro: 'Pro', business: 'Business', enterprise: 'Enterprise' };

export default function QuizRegenerateButton({ section, lessonTitle = '', onReplace }) {
  const { API } = useContext(AuthContext);
  const [open,    setOpen]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [topic,   setTopic]   = useState('');
  const [tier,    setTier]    = useState('pro');
  const [count,   setCount]   = useState(3);

  // Seed the topic when the popover opens so the admin doesn't have to
  // retype it. Prefer the section title (strips the "AI-generated quiz —"
  // marker if present), otherwise fall back to the lesson title.
  const handleOpenChange = (v) => {
    setOpen(v);
    if (v) {
      const seed =
        (section?.title || '').replace(/^AI-generated quiz\s*—\s*/i, '').trim() ||
        lessonTitle ||
        '';
      setTopic(seed);
    }
  };

  const run = async () => {
    if (!topic.trim() || topic.trim().length < 3) {
      toast.error('Please enter a topic (at least 3 characters).');
      return;
    }
    const c = Math.max(1, Math.min(10, parseInt(count, 10) || 3));
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/ai/regenerate-quiz`, {
        topic:       topic.trim(),
        target_tier: tier,
        count:       c,
      });
      const items = Array.isArray(res.data?.quiz) ? res.data.quiz : [];
      if (items.length === 0) {
        toast.error('AI produced no usable quiz items. Try again.');
        return;
      }
      // Map into the Quiz Builder shape used elsewhere in the lesson.
      const questions = items.map((q) => {
        const options = Array.isArray(q.options) ? q.options.slice(0, 4) : ['', '', '', ''];
        while (options.length < 4) options.push('');
        const idx = Number.isInteger(q.answer_index) ? q.answer_index : 0;
        const safeIdx = Math.max(0, Math.min(options.length - 1, idx));
        return {
          question:       q.question || '',
          type:           'multiple_choice',
          options,
          correct_answer: options[safeIdx] || '',
          explanation:    q.explanation || '',
        };
      });
      onReplace?.(questions);
      toast.success(`Refreshed ${questions.length} question${questions.length === 1 ? '' : 's'}`);
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Regeneration failed';
      toast.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          title="Regenerate this quiz with AI (keeps your lesson body intact)"
          data-testid={`quiz-regen-btn-${section?.id ?? ''}`}
        >
          <RefreshCw className="w-4 h-4 text-amber-500" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 space-y-3"
        data-testid="quiz-regen-popover"
      >
        <div>
          <p className="font-semibold text-sm">Regenerate this quiz</p>
          <p className="text-xs text-slate-500">
            Only the questions in this section change. Your lesson body and other sections stay untouched.
          </p>
        </div>
        <div>
          <Label className="text-xs">Topic</Label>
          <Input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="e.g. Freeze drying strawberries"
            data-testid="quiz-regen-topic"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Tier</Label>
            <Select value={tier} onValueChange={setTier}>
              <SelectTrigger data-testid="quiz-regen-tier">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(TIER_LABEL).map(([v, l]) => (
                  <SelectItem key={v} value={v}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Count</Label>
            <Input
              type="number"
              min={1}
              max={10}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              data-testid="quiz-regen-count"
            />
          </div>
        </div>
        <Button
          onClick={run}
          disabled={loading}
          className="w-full"
          data-testid="quiz-regen-run-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {loading ? 'Regenerating…' : 'Regenerate'}
        </Button>
      </PopoverContent>
    </Popover>
  );
}
